/**
 * Venstre sidepanel med tre faner i stedet for én lang scroll:
 *  - Bygg:        uplasserte reoler + enkeltlokasjoner, med søk og antall-badges
 *  - Plassert:    liste over plasserte reoler
 *  - Innstillinger: lagerdimensjoner + plantegning-underlag
 */

import React, { useMemo, useState } from 'react';
import {
  Package,
  ListChecks,
  Settings,
  Search,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  X,
} from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { SidebarTab } from '../hooks/useLayoutReducer';
import PlacedRacksList from './PlacedRacksList';
import UnplacedRacksList from './UnplacedRacksList';
import UnplacedBinsList from './UnplacedBinsList';
import ObjectPalette from './ObjectPalette';
import SettingsPanel from './SettingsPanel';

/** Kollapsbar seksjon med overskrift og antall-badge */
const SidebarSection: React.FC<{
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, count, collapsed, onToggle, children }) => (
  <div className="border-b border-gray-100">
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-50 transition-colors"
      aria-expanded={!collapsed}
    >
      {collapsed
        ? <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
        : <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />}
      <span className="truncate">{title}</span>
      <span className="ml-auto bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 text-xs font-medium normal-case tracking-normal">
        {count}
      </span>
    </button>
    {!collapsed && children}
  </div>
);

const EditorSidebar: React.FC = () => {
  const { state, dispatch } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout, unplacedRacks, specialBins, binPositions, sidebarTab } = state;

  const [search, setSearch] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Enkeltlokasjoner som ennå ikke er plassert (samme utledning som UnplacedBinsList)
  const unplacedBins = useMemo(() => {
    const placed = new Set(binPositions.map(p => p.binCode));
    return specialBins.filter(b => !placed.has(b.binCode));
  }, [specialBins, binPositions]);

  // Søkefiltrerte antall til seksjons-badges (visning — selve filtreringen skjer i listene)
  const q = search.trim().toLowerCase();
  const visibleRackCount = q
    ? unplacedRacks.filter(r => r.rackNumber.toLowerCase().includes(q)).length
    : unplacedRacks.length;
  const visibleBinCount = q
    ? unplacedBins.filter(b =>
        b.binCode.toLowerCase().includes(q) || (b.description1 ?? '').toLowerCase().includes(q)
      ).length
    : unplacedBins.length;

  const buildCount = unplacedRacks.length + unplacedBins.length;
  const placedCount = layout?.racks.length ?? 0;

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const tabs: Array<{ id: SidebarTab; label: string; icon: React.ReactNode; badge: number | null }> = [
    {
      id: 'build',
      label: t('manager.modules.warehouseLayout.sidebar.tabBuild', {}, 'Bygg'),
      icon: <Package className="w-4 h-4" />,
      badge: buildCount,
    },
    {
      id: 'placed',
      label: t('manager.modules.warehouseLayout.sidebar.tabPlaced', {}, 'Plassert'),
      icon: <ListChecks className="w-4 h-4" />,
      badge: placedCount,
    },
    {
      id: 'settings',
      label: t('manager.modules.warehouseLayout.sidebar.tabSettings', {}, 'Innstillinger'),
      icon: <Settings className="w-4 h-4" />,
      badge: null,
    },
  ];

  return (
    <div className="w-72 bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
      {/* Fane-linje */}
      <div className="flex border-b border-gray-200 flex-shrink-0" role="tablist">
        {tabs.map(tab => {
          const active = sidebarTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={active}
              onClick={() => dispatch({ type: 'SET_SIDEBAR_TAB', tab: tab.id })}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-xs font-medium border-b-2 transition-colors ${
                active
                  ? 'border-blue-600 text-blue-700 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              title={tab.label}
            >
              <span className="relative">
                {tab.icon}
                {tab.badge !== null && tab.badge > 0 && (
                  <span className={`absolute -top-1.5 -right-3 min-w-[1.1rem] px-1 rounded-full text-[10px] leading-4 text-center font-semibold ${
                    active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </span>
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Fane-innhold */}
      <div className="flex-1 overflow-y-auto">
        {sidebarTab === 'build' && (
          <div>
            {buildCount === 0 ? (
              /* Tomtilstand: alt er plassert */
              <div className="px-4 py-10 text-center">
                <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700">
                  {t('manager.modules.warehouseLayout.sidebar.allPlaced', {}, 'Alle reoler er plassert')}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t('manager.modules.warehouseLayout.sidebar.allPlacedHint', {}, 'Bruk «Plassert»-fanen for å justere, eller dra elementer direkte i 2D-visningen.')}
                </p>
              </div>
            ) : (
              <>
                {/* Søkefelt */}
                <div className="p-3 pb-2">
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder={t('manager.modules.warehouseLayout.sidebar.searchPlaceholder', {}, 'Søk reol eller lokasjon...')}
                      className="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title={t('common.buttons.clear', {}, 'Tøm')}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {unplacedRacks.length > 0 && (
                  <SidebarSection
                    title={t('manager.modules.warehouseLayout.sidebar.sectionUnplacedRacks', {}, 'Uplasserte reoler')}
                    count={visibleRackCount}
                    collapsed={collapsedSections.has('racks')}
                    onToggle={() => toggleSection('racks')}
                  >
                    <UnplacedRacksList filter={search} />
                  </SidebarSection>
                )}

                {unplacedBins.length > 0 && (
                  <SidebarSection
                    title={t('manager.modules.warehouseLayout.sidebar.sectionSingleBins', {}, 'Enkeltlokasjoner')}
                    count={visibleBinCount}
                    collapsed={collapsedSections.has('bins')}
                    onToggle={() => toggleSection('bins')}
                  >
                    <UnplacedBinsList filter={search} />
                  </SidebarSection>
                )}
              </>
            )}

            {/* Objekter (søyle/port/stasjon/lading/sonemarkør) — alltid tilgjengelig,
                også når alle reoler/lokasjoner er plassert. Badge = antall plasserte. */}
            <SidebarSection
              title={t('manager.modules.warehouseLayout.objects.sectionTitle', {}, 'Objekter')}
              count={layout?.objects.length ?? 0}
              collapsed={collapsedSections.has('objects')}
              onToggle={() => toggleSection('objects')}
            >
              <ObjectPalette />
            </SidebarSection>
          </div>
        )}

        {sidebarTab === 'placed' && (
          placedCount === 0 ? (
            /* Tomtilstand: ingenting plassert ennå */
            <div className="px-4 py-10 text-center">
              <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">
                {t('manager.modules.warehouseLayout.sidebar.noPlaced', {}, 'Ingen reoler er plassert ennå')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('manager.modules.warehouseLayout.sidebar.noPlacedHint', {}, 'Gå til «Bygg»-fanen for å plassere reoler.')}
              </p>
            </div>
          ) : (
            <PlacedRacksList />
          )
        )}

        {sidebarTab === 'settings' && <SettingsPanel />}
      </div>
    </div>
  );
};

export default EditorSidebar;
